import { ComponentProps, useEffect, useRef, useState } from 'react';
import { PhoneNumberVerify } from './PhoneNumberVerify';
import { useLoginWithPhone } from './useLoginWithPhone';

type Props = ComponentProps<typeof PhoneNumberVerify>;

export const usePhoneNumberVerify = ({ phoneNumber }: Props) => {
  const [verificationCode, setVerificationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const singleInputRef = useRef<HTMLInputElement | null>(null);

  const formattedPhoneNumber = phoneNumber.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');

  useEffect(() => {
    singleInputRef.current?.focus();
  }, []);

  /**
   * 単一入力フィールドの値変更を処理する
   * 数字のみを受け入れ、最大6桁まで制限する
   *
   * @param e - 変更イベント
   */
  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const cleanedValue = value.replace(/\D/g, '').slice(0, 6);
    setVerificationCode(cleanedValue);
  };

  /**
   * キーダウンイベントを処理する
   * Backspaceキーで文字を削除する
   *
   * @param e - キーボードイベント
   */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && verificationCode.length > 0) {
      e.preventDefault();
      setVerificationCode(verificationCode.slice(0, -1));
    }
  };

  /**
   * WebOTP APIを使用してSMSから認証コードを自動読み取りする
   * ブラウザがWebOTP APIをサポートしている場合のみ実行される
   */
  const handleWebOTPRequest = async () => {
    if ('OTPCredential' in window && 'credentials' in navigator) {
      try {
        const abortController = new AbortController();

        setTimeout(() => abortController.abort(), 30000);

        const otpCredential = await (
          navigator.credentials as unknown as {
            get: (options: { otp: { transport: string[] }; signal: AbortSignal }) => Promise<{ code?: string }>;
          }
        ).get({
          otp: { transport: ['sms'] },
          signal: abortController.signal,
        });

        if (otpCredential?.code) {
          const cleanedCode = otpCredential.code.replace(/\D/g, '').slice(0, 6);
          setVerificationCode(cleanedCode);
        }
      } catch (error) {
        console.debug('WebOTP API error:', error);
      }
    }
  };

  // ログイン
  const { loginWithPhone } = useLoginWithPhone({ setIsLoading });
  const handleSubmit = async () => {
    await loginWithPhone({ phoneNumber, verificationCode });
  };

  return {
    verificationCode,
    isLoading,
    formattedPhoneNumber,
    handleCodeChange,
    handleKeyDown,
    handleSubmit,
    handleWebOTPRequest,
    singleInputRef,
  };
};
